import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { switchMap, first } from 'rxjs/operators';

import { AHheatPumpDAY, AHheatPumpRTData, HeatPumpDAY, HeatPumpRTData, Range } from '../telemetry';
import { getTelemetryRangeWithTzOnHour, maskIdHeatpump, removeHeatpumpMaskFromDeviceList } from '../utils';
import { GraphqlApiService } from '../graphql-api/graphql-api.service';

@Injectable({ providedIn: 'root' })
export class HeatpumpService {
  constructor(
    private readonly graphqlApiService: GraphqlApiService,
  ) {}

  async getHeatPumpDataByRange(
    serial: string,
    date: string,
    tz: string,
    refresh = false,
    loggerId?: string
  ): Promise<Partial<HeatPumpRTData>[]> {
    const range = getTelemetryRangeWithTzOnHour(date, tz);

    const query = gql`
      query getHeatPumpDataByRange($serial: String!, $range: Range!, $loggerId: String) {
        getHeatPumpDataByRange(serial: $serial, range: $range, loggerId: $loggerId) {
          ... on AHheatPumpRTData {
            HP_E_EG
            HP_E_ET
            CURR_OP_MODE
            DHWOn
            DHWSetTemp
            DHW_TEMP
            ENV_TEMP
            GwId
            HP_E_PWR
            globStatus
            created
            climSetTempHeatingA
            climSetTempCoolingA
            climOpMode
            climOnA
            TZ
            TS
            SK
            RET_TEMP
            PK
            OUT_TEMP
            IANA
            HP_T_PWR
            HP_T_ET
            HP_T_EG
          }
          __typename
        }
      }
    `;

    const result = await this.graphqlApiService.client$.pipe(
      first(),
      switchMap(async client => {
        if (!client) {
          throw new Error('GraphQL client not available');
        }
        const res = await client.query<{ getHeatPumpDataByRange: Partial<HeatPumpRTData>[] }>({
          query,
          variables: { serial: serial.toUpperCase(), range, loggerId },
          fetchPolicy: refresh ? 'network-only' : 'cache-first',
        });
        return res.data.getHeatPumpDataByRange;
      })
    ).toPromise();

    return result;
  }

  private formatYMD(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  async getHeatPumpMonthlyAggregation(
    serial: string,
    date: Date,
    refresh = false,
    loggerId?: string
  ): Promise<Partial<HeatPumpDAY>[]> {
    const y = date.getFullYear();
    const m = date.getMonth();
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);

    const range: Range = {
      from: this.formatYMD(firstDay),
      to: this.formatYMD(lastDay),
    };

    return this.getHeatPumpDataByAggregation(serial, range, refresh, loggerId);
  }


  async getHeatPumpYearlyAggregation(
    serial: string,
    date: Date,
    refresh = false,
    loggerId?: string
  ): Promise<Partial<HeatPumpDAY>[]> {
    const y = date.getFullYear();
    const firstDay = new Date(y, 0, 1);
    const lastDay = new Date(y, 11, 31);

    const range: Range = {
      from: this.formatYMD(firstDay),
      to: this.formatYMD(lastDay),
    };

    return this.getHeatPumpDataByAggregation(serial, range, refresh, loggerId);
  }

  async fetchHeatpumpsAggregation(
      deviceIds: string[],
      range: Range,
      tz: string,
      loggerId?: string,
      refresh: boolean = false,
    ): Promise<{ [key: string]: HeatPumpDAY[] }> {

      const query = gql`${this.BuildHeatPumpAggregationQuery(deviceIds.map(id => ({ id })), range, loggerId)}`;

      const client = await this.graphqlApiService.client$.pipe(first()).toPromise();
      if (!client) throw new Error('GraphQL client not available');

      const result = await client.query<{ [key: string]: HeatPumpDAY[] }>({
        query,
        fetchPolicy: refresh ? 'network-only' : 'cache-first',
      });

      const data = result.data || {} as { [key: string]: HeatPumpDAY[] };

      return removeHeatpumpMaskFromDeviceList(data);
    }

    async fetchHeatpumpsRange(
      deviceIds: string[],
      date: string,
      tz: string,
      loggerId?: string,
      refresh: boolean = false,
    ): Promise<{ [key: string]: AHheatPumpRTData[] }> {

      const range: Range = getTelemetryRangeWithTzOnHour(date, tz);


      const query = gql`${this.BuildHeatPumpRangeQuery(deviceIds.map(id => ({ id })), range, loggerId)}`;

      const client = await this.graphqlApiService.client$.pipe(first()).toPromise();
      if (!client) throw new Error('GraphQL client not available');

      const result = await client.query<{ [key: string]: AHheatPumpRTData[] }>({
        query,
        fetchPolicy: refresh ? 'network-only' : 'cache-first',
      });

      const data = result.data || {} as { [key: string]: AHheatPumpRTData[] };

      return removeHeatpumpMaskFromDeviceList(data);
    }

  /**
   * Build multi-device aggregation query for getHeatPumpDataByAggregation
   * Uses device.id as both alias and serial string (uppercased in the query)
   */
  private BuildHeatPumpAggregationQuery = (
    devices: { id: string }[],
    range: Range,
    loggerId?: string
  ): string => {
    const rangeStr = JSON.stringify(range).replace(/"(\w+)":/g, '$1:');

    const deviceQueries = devices.map((device) => {
      const serial = device.id.toUpperCase(); // keep same behaviour as single-serial API

      const args: string[] = [
        `serial: "${serial}"`,
        `range: ${rangeStr}`,
      ];
      if (loggerId) {
        args.push(`loggerId: "${loggerId}"`);
      }

      return `
        ${maskIdHeatpump(serial)}: getHeatPumpDataByAggregation(${args.join(', ')}) {
          ... on AHheatPumpDAY {
            PK
            SK
            created
            TS
            RET_TEMP
            OUT_TEMP
            ENV_TEMP
            IANA
            HP_T_ET
            HP_T_EG
            HP_E_ET
            HP_E_EG
            GwId
            DHW_TEMP
            CURR_OP_MODE
          }
          __typename
        }
      `;
    });

    return `query { ${deviceQueries.join('')} }`;
  };


  /**
   * Build multi-device range query for getHeatPumpDataByRange
   * Uses device.id as both alias and serial string (uppercased in the query)
   */
  private BuildHeatPumpRangeQuery = (
    devices: { id: string }[],
    range: Range,
    loggerId?: string
  ): string => {
    const rangeStr = JSON.stringify(range).replace(/"(\w+)":/g, '$1:');

    const deviceQueries = devices.map((device) => {
      const serial = device.id.toUpperCase(); // keep same behaviour as single-serial API

      const args: string[] = [
        `serial: "${serial}"`,
        `range: ${rangeStr}`,
      ];
      if (loggerId) {
        args.push(`loggerId: "${loggerId}"`);
      }

      return `
        ${maskIdHeatpump(serial)}: getHeatPumpDataByRange(${args.join(', ')}) {
          ... on AHheatPumpRTData {
            HP_E_EG
            HP_E_ET
            CURR_OP_MODE
            DHWOn
            DHWSetTemp
            DHW_TEMP
            ENV_TEMP
            GwId
            HP_E_PWR
            globStatus
            created
            climSetTempHeatingA
            climSetTempCoolingA
            climOpMode
            climOnA
            TZ
            TS
            SK
            RET_TEMP
            PK
            OUT_TEMP
            IANA
            HP_T_PWR
            HP_T_ET
            HP_T_EG
          }
          __typename
        }
      `;
    });

    return `query { ${deviceQueries.join('')} }`;
  };


  async getHeatPumpDataByAggregation(
    serial: string,
    range: Range,
    refresh = false,
    loggerId?: string
  ): Promise<Partial<HeatPumpDAY>[]> {
    const query = gql`
      query getHeatPumpDataByAggregation($serial: String!, $range: Range!, $loggerId: String) {
        getHeatPumpDataByAggregation(serial: $serial, range: $range, loggerId: $loggerId) {
          ... on AHheatPumpDAY {
            PK
            SK
            created
            TS
            RET_TEMP
            OUT_TEMP
            ENV_TEMP
            IANA
            HP_T_ET
            HP_T_EG
            HP_E_ET
            HP_E_EG
            GwId
            DHW_TEMP
            CURR_OP_MODE
          }
          __typename
        }
      }
    `;

    const result = await this.graphqlApiService.client$.pipe(
      first(),
      switchMap(async client => {
        if (!client) {
          throw new Error('GraphQL client not available');
        }
        const res = await client.query<{ getHeatPumpDataByAggregation: Partial<HeatPumpDAY>[] }>({
          query,
          variables: { serial: serial.toUpperCase(), range, loggerId },
          fetchPolicy: refresh ? 'network-only' : 'cache-first',
        });
        return res.data.getHeatPumpDataByAggregation;
      })
    ).toPromise();

    return result;
  }

  async getCurrentHeatPumpData(
    serial: string,
    refresh = false,
    loggerId?: string
  ): Promise<Partial<HeatPumpDAY>> {
    const query = gql`
      query getHeatPumpDataByAggregation($serial: String!, $loggerId: String) {
        getHeatPumpDataByAggregation(serial: $serial, loggerId: $loggerId) {
          ... on AHheatPumpDAY {
            PK
            SK
            created
            TS
            RET_TEMP
            OUT_TEMP
            ENV_TEMP
            IANA
            HP_T_ET
            HP_T_EG
            HP_E_ET
            HP_E_EG
            GwId
            DHW_TEMP
            CURR_OP_MODE
          }
          __typename
        }
      }
    `;

    const result = await this.graphqlApiService.client$.pipe(
      first(),
      switchMap(async client => {
        if (!client) {
          throw new Error('GraphQL client not available');
        }
        const result = await client.query<{ getHeatPumpDataByAggregation: AHheatPumpDAY[] }>({
          query,
          variables: { serial: serial.toUpperCase(), loggerId },
          fetchPolicy: refresh ? 'network-only' : 'cache-first',
        });
        return result.data.getHeatPumpDataByAggregation.length ? result.data.getHeatPumpDataByAggregation[0] : {};
      })
    ).toPromise();
    return result as Partial<HeatPumpDAY>;
  }

}